import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import client, { imgUrl, logoUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import MagazineLayout from "@/components/MagazineLayout";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { PortalTour } from "@/components/PortalTour";
import { ProgressChecklist } from "@/components/ProgressChecklist";
import { issueNoFor, cycleIndexFor, editionStart } from "@/lib/issue";
import { toast } from "sonner";
import { Upload, Check, Clock, Sparkles, CreditCard, Image as ImageIcon, Bell, FileText, X, Search, TrendingUp, Globe } from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";

const CATEGORIES = ["Design Studio", "Bakery", "Tailoring", "Wine Bar", "Restaurant", "Cafe", "Salon", "Fitness", "Consulting", "Retail", "Photography", "Florist", "Other"];
const BUSINESS_CATS = ["Food & Drink", "Beauty & Health", "Retail", "Online", "Services", "Arts & Entertainment"];

export default function Portal() {
  const { user, member, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const isPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";
  const [polling, setPolling] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [notify, setNotify] = useState(false);
  const [billing, setBilling] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);

  const loadBilling = useCallback(() => {
    client.get("/billing").then((r) => setBilling(r.data)).catch(() => setBilling(null));
  }, []);

  useEffect(() => {
    if (user?.subscription_status === "active") loadBilling();
  }, [user?.subscription_status, loadBilling]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

  const cancelMembership = async () => {
    setBillingBusy(true);
    try {
      const { data } = await client.post("/billing/cancel");
      toast.success(`Membership will end on ${fmtDate(data.ends_at)}. You keep full access until then.`);
      setCancelConfirm(false);
      loadBilling();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel — please contact hello@smemagazine.co.uk");
    }
    setBillingBusy(false);
  };

  const resumeMembership = async () => {
    setBillingBusy(true);
    try {
      const { data } = await client.post("/billing/resume");
      toast.success(`Welcome back — your membership renews on ${fmtDate(data.renews_at)}.`);
      loadBilling();
    } catch { toast.error("Couldn't resume — please contact hello@smemagazine.co.uk"); }
    setBillingBusy(false);
  };

  const pollStatus = useCallback(async (sid, attempt = 0) => {
    if (attempt > 6) { setPolling(false); toast.error("Payment status timed out. Refresh to check."); return; }
    try {
      const { data } = await client.get(`/checkout/status/${sid}`);
      if (data.payment_status === "paid") {
        toast.success("Welcome to S/ME. Membership active.");
        await refresh();
        setPolling(false);
        window.history.replaceState({}, "", "/portal");
        return;
      }
      if (data.status === "expired") { setPolling(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch { setTimeout(() => pollStatus(sid, attempt + 1), 2000); }
  }, [refresh]);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) { setPolling(true); pollStatus(sid); }
  }, [pollStatus]);

  // guest pass / deep-link: land straight on the profile form
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("focus") !== "profile") return;
    if (!form) return;
    const t = setTimeout(() => {
      document.querySelector('[data-testid="onboarding-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState({}, "", "/portal");
    }, 600);
    return () => clearTimeout(t);
  }, [form]);

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/portal" + (isPreview ? "?preview=1" : ""));
    if (!loading && user?.role === "admin" && !isPreview) navigate("/admin");
  }, [loading, user, navigate, isPreview]);

  useEffect(() => {
    if (form) return;
    if (member) {
      setForm({
        business_name: member.business_name || "", owner_name: member.owner_name || "",
        borough: member.borough || "", category: member.category || "", business_category: member.business_category || "Services",
        address: member.address || "", display_address: member.display_address !== false, bio: member.bio || "", personal_bio: member.personal_bio || "", business_bio: member.business_bio || "",
        services: member.services || "", offers: member.offers || "", phone: member.phone || "",
        mobile: member.mobile || "", display_phone: member.display_phone !== false, landline: member.landline || "", use_landline: !!member.use_landline,
        email: member.email || "", website: member.website || "", instagram: member.instagram || "", facebook: member.facebook || "", tiktok: member.tiktok || "", linkedin: member.linkedin || "", ref_notes: member.ref_notes || "",
      });
    } else if (user) {
      setForm({ business_name: "", owner_name: user.name || "", borough: "", category: "", business_category: "Services", address: "", display_address: true, bio: "", personal_bio: "", business_bio: "", services: "", offers: "", phone: "", mobile: "", display_phone: true, landline: "", use_landline: false, email: "", website: "", instagram: "", facebook: "", tiktok: "", linkedin: "", ref_notes: "" });
    }
  }, [member, user]);

  useEffect(() => { if (member) setNotify(!!member.notify_email); }, [member]);

  // Draft auto-save: restore once, then quietly save as they type
  const draftLoaded = useRef(false);
  const formRuns = useRef(0);
  const draftTimer = useRef(null);
  const [draftState, setDraftState] = useState("");
  useEffect(() => {
    if (!form || draftLoaded.current) return;
    draftLoaded.current = true;
    client.get("/members/me/draft").then((r) => {
      if (r.data?.data) {
        const clean = Object.fromEntries(Object.entries(r.data.data).filter(([, v]) => v !== "" && v !== null && v !== undefined));
        setForm((f) => ({ ...f, ...clean }));
        formRuns.current = -1;
      }
    }).catch(() => {});
  }, [form]);
  useEffect(() => {
    if (!form) return;
    if (isPreview) return; // preview mode: never write
    formRuns.current += 1;
    if (formRuns.current <= 1) return;
    setDraftState("saving");
    clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(async () => {
      try { await client.post("/members/me/draft", { data: form }); setDraftState("saved"); }
      catch { setDraftState(""); }
    }, 1500);
    return () => clearTimeout(draftTimer.current);
  }, [form, isPreview]);

  const toggleNotify = async () => {
    const val = !notify;
    setNotify(val);
    try {
      await client.post("/members/me/notify", { notify_email: val });
      toast.success(val ? "We'll email you the moment your next drop is ready." : "Notifications off.");
    } catch { setNotify(!val); toast.error("Could not update. Try again."); }
  };

  const [polishing, setPolishing] = useState("");
  const polish = async (field) => {
    const base = `${form.business_name || ""} ${form.category || ""} ${form.borough || ""}. ${form.business_bio || ""} ${form.personal_bio || ""} ${form.services || ""} ${form.bio || ""}`.trim();
    const briefs = {
      personal_bio: `Write a warm, characterful 2-3 sentence bio about ${form.owner_name || "the owner"}, the person behind ${form.business_name || "the business"}. Notes: ${form.personal_bio || base}`,
      business_bio: `Write a sharp 2-3 sentence bio about ${form.business_name || "the business"} — what it does and what makes it genuinely special. Notes: ${form.business_bio || base}`,
      services: `List the key services/products of ${form.business_name || "the business"} as a concise bullet list, one short item per line, each starting "- ". 4-8 items, no intro. Base it on: ${form.services || base}`,
    };
    setPolishing(field);
    try {
      const r = await client.post("/ai/assist", { field, instruction: briefs[field], context: base });
      setForm((f) => ({ ...f, [field]: r.data.text }));
      toast.success("Polished by the desk — tweak as you like, then save.");
    } catch (e) { toast.error(e.response?.data?.detail || "The desk is busy — try again shortly."); }
    setPolishing("");
  };
  const PolishBtn = ({ field }) => (
    <button type="button" onClick={() => polish(field)} disabled={!!polishing} className="overline text-ink/60 border border-ink/30 px-3 py-1.5 inline-flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid={`polish-${field}`}>
      {polishing === field ? <Clock size={13} className="animate-pulse" /> : <Sparkles size={13} />} Ask the desk to write this
    </button>
  );

  const [fieldErrors, setFieldErrors] = useState({});
  const [justSaved, setJustSaved] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingLogo, setPendingLogo] = useState("");
  const REQUIRED_FIELDS = { business_name: "Business name", owner_name: "Your name", address: "Full business address", email: "Email", mobile: "Mobile number" };
  const save = async (e) => {
    e.preventDefault();
    if (isPreview) { toast.info("Preview mode — nothing is saved."); return; }
    const errs = {};
    Object.entries(REQUIRED_FIELDS).forEach(([k, label]) => { if (!String(form[k] || "").trim()) errs[k] = `${label} is required`; });
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setJustSaved(false);
      document.querySelector('[data-testid="onboarding-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setSaving(true);
    try {
      const totalPhotos = (member?.photos || []).length + pendingPhotos.length;
      await client.post("/members/onboard", form);
      // Carry the member's website photo picks + logo straight through to the studio
      if (pendingPhotos.length) {
        try { await client.post("/members/me/import-photos", { urls: pendingPhotos }); setPendingPhotos([]); } catch { /* site blocked downloads */ }
      }
      if (pendingLogo) {
        try { await client.post("/members/me/import-logo", { url: pendingLogo }); setPendingLogo(""); } catch { /* non-fatal */ }
      }
      if (totalPhotos < 3) {
        refresh();
        toast.info("Profile saved. Now add at least 3 photos below — then hit Send to the studio.", { duration: 8000 });
        setTimeout(() => document.querySelector('[data-testid="photos-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
        return;
      }
      if (!member?.imagery_preference) {
        refresh();
        toast.info("One last choice — tell us how to handle your photos (just below them), then hit Send to the studio.", { duration: 8000 });
        setTimeout(() => document.querySelector('[data-testid="imagery-preference"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
        return;
      }
      setJustSaved(true);
      navigate("/portal/sent");
      refresh();
    } catch { toast.error("Could not save. Please try again."); }
    finally { setSaving(false); }
  };

  const [uploadingCover, setUploadingCover] = useState("");
  const uploadOwnCover = async (files, target) => {
    if (!member) { toast.error("Save your profile first."); return; }
    if (!files?.[0]) return;
    setUploadingCover(target);
    const fd = new FormData(); fd.append("file", files[0]);
    try {
      await client.post(`/members/me/cover-upload?target=${target}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Your own photo is now your " + (target === "directory_image" ? "Directory" : "Editorial") + " cover.");
    } catch { toast.error("Upload failed."); }
    setUploadingCover("");
  };

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [savingInt, setSavingInt] = useState(false);
  useEffect(() => {
    client.get("/members/me/interview-questions").then((r) => setQuestions(r.data.questions || [])).catch(() => {});
  }, []);
  useEffect(() => { if (member?.interview_answers) setAnswers(member.interview_answers); }, [member]);
  const saveInterview = async () => {
    setSavingInt(true);
    try { await client.post("/members/me/interview", { answers }); toast.success("Interview answers saved — we'll craft your 5 Questions feature."); await refresh(); }
    catch { toast.error("Could not save answers."); }
    setSavingInt(false);
  };

  const uploadPhotos = async (files) => {
    if (!member) { toast.error("Save your profile first."); return; }
    setUploading(true);
    const fd = new FormData();
    Array.from(files).slice(0, 8).forEach((f) => fd.append("files", f));
    try {
      const r = await client.post("/members/me/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      if (r.data.skipped > 0) toast.warning(`Portrait slots are limited to 5 — ${r.data.skipped} photo${r.data.skipped === 1 ? " was" : "s were"} not added. Remove one to swap, or add extras to "Your Work & Products" below.`, { duration: 9000 });
      else toast.success("Photos uploaded.");
    } catch { toast.error("Upload failed."); }
    setUploading(false);
  };

  const [uploadingWork, setUploadingWork] = useState(false);
  const uploadWork = async (files) => {
    if (!member) { toast.error("Save your profile first."); return; }
    if (!files?.length) return;
    setUploadingWork(true);
    const fd = new FormData();
    Array.from(files).slice(0, 10).forEach((f) => fd.append("files", f));
    try {
      const r = await client.post("/members/me/work-photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      if (r.data.skipped > 0) toast.warning(`Added what fitted — the work gallery holds 20 photos and ${r.data.skipped} didn't fit.`);
      else toast.success("Work photos added — the desk can now use them across your features.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    setUploadingWork(false);
  };

  const uploadLogo = async (files) => {
    if (!member) { toast.error("Save your profile first."); return; }
    if (!files?.[0]) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", files[0]);
    try {
      await client.post("/members/me/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Logo uploaded.");
    } catch { toast.error("Logo upload failed."); }
    setUploadingLogo(false);
  };

  const [uploadingVenue, setUploadingVenue] = useState(false);
  const [makingVenue, setMakingVenue] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const uploadRefDocs = async (files) => {
    if (!member) { toast.error("Save your profile first."); return; }
    if (!files?.length) return;
    setUploadingDocs(true);
    const fd = new FormData();
    Array.from(files).slice(0, 5).forEach((f) => fd.append("files", f));
    try { await client.post("/members/me/ref-docs", fd, { headers: { "Content-Type": "multipart/form-data" } }); await refresh(); toast.success("Documents added — our editorial desk will read them."); }
    catch { toast.error("Upload failed — PDF, DOCX or TXT only."); }
    setUploadingDocs(false);
  };
  const deleteRefDoc = async (id) => {
    try { await client.delete(`/members/me/ref-docs/${id}`); await refresh(); toast.success("Document removed."); }
    catch { toast.error("Could not remove."); }
  };
  const uploadVenue = async (files) => {
    if (!member) { toast.error("Save your profile first."); return; }
    if (!files?.length) return;
    setUploadingVenue(true);
    const fd = new FormData();
    Array.from(files).slice(0, 5).forEach((f) => fd.append("files", f));
    try { await client.post("/members/me/venue-photos", fd, { headers: { "Content-Type": "multipart/form-data" } }); await refresh(); toast.success("Venue photos uploaded."); }
    catch { toast.error("Upload failed."); }
    setUploadingVenue(false);
  };
  const genVenue = async () => {
    setMakingVenue(true);
    try { await client.post("/members/me/generate-venue", {}); await refresh(); toast.success("Your S/ME venue imagery is ready."); }
    catch { toast.error("Venue generation failed, please retry."); }
    setMakingVenue(false);
  };

  if (loading || !form) return <MagazineLayout><div className="h-screen flex items-center justify-center"><p className="overline text-ink/40">Loading…</p></div></MagazineLayout>;

  const active = user?.subscription_status === "active";
  const photos = member?.photos || [];
  const pkg = member?.package || {};
  const hasPackage = !!pkg.feature;
  const yearPlan = buildYearPlan(member);

  return (
    <MagazineLayout>
      <div className="pt-[68px] bg-cream min-h-screen">
        {isPreview && (
          <div className="bg-ink text-cream px-5 py-3 sticky top-[68px] z-30 flex items-center justify-center gap-3" data-testid="portal-preview-banner">
            <span className="w-2 h-2 rounded-full bg-gloss-gold animate-pulse" />
            <p className="font-mono text-[11px] uppercase tracking-widest">Editor preview — nothing on this page is saved</p>
          </div>
        )}
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-16 sm:pt-20 pb-40 sm:pb-48">
          <p className="overline text-gloss-silver mb-3">Member Portal</p>
          <h1 className="font-serif text-4xl sm:text-6xl tracking-tight">Hello, {(form?.owner_name || user?.name || "")?.split(" ")[0]}</h1>
          <p className="font-sans text-lg text-ink/70 mt-5 max-w-2xl leading-relaxed">
            Welcome to <span className="font-serif italic">S/ME</span> — a business-visibility magazine with a <span className="text-ink">year-long mindset</span>, packaged as a stylish, editorial-grade publication. We don't run one advert and disappear. Across twelve months we turn you into an editorial star — features, covers, adverts, interviews and social — so the whole UK keeps discovering you, all year, every year.
          </p>

          {/* What membership includes */}
          <div className="mt-8 border border-ink/15 bg-secondary p-6 sm:p-8" data-testid="membership-includes">
            <p className="overline text-gloss-silver mb-4">What your membership includes</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {[
                "Permanent Cover Member profile — your always-on presence in the magazine",
                "Permanent Directory listing, optimised to be found",
                "An Editorial Feature — your story as a beautiful magazine piece",
                "A Spotlight Feature (or the Cover Star slot fronting the homepage)",
                "A rapid-fire \"5 Questions\" published interview",
                "One Premium Advert, placed naturally through the magazine",
                "A feature in the S/ME newsletter",
                "Your feature cut into a ready-to-post reel — article scroll or photo film",
                "Reels, Stories & social tiles for Instagram, Facebook + TikTok — posted with your handle tagged and a collaborate invite",
                "A blog feature each year",
                "Unlimited Noticeboard listings whenever you have an offer",
              ].map((t) => (
                <div key={t} className="flex items-start gap-2.5">
                  <Check size={15} className="text-gloss-gold mt-1 shrink-0" />
                  <span className="font-sans text-sm text-ink/80">{t}</span>
                </div>
              ))}
            </div>
            <p className="font-sans text-xs text-ink/55 mt-5">Spread fairly across the year — <b>one fresh moment in the magazine every month</b>. Each feature runs for a full fortnight edition, then rests in the archive while your next one is prepared.</p>
          </div>

          {polling && <div className="mt-6 bg-ink text-cream px-5 py-4 font-sans flex items-center gap-3"><Clock size={18} className="animate-pulse" /> Confirming your payment…</div>}

          {/* Your part is small — top-level comms */}
          {active && (
            <div className="mt-8 bg-ink text-cream p-6 sm:p-8" data-testid="your-part-note">
              <p className="overline text-gloss-gold mb-3">How this works</p>
              <p className="font-serif text-2xl sm:text-3xl leading-tight">Your part is small. Ours is everything else.</p>
              <p className="font-sans text-sm text-cream/70 mt-3 leading-relaxed max-w-2xl">Fill in the few fields marked <span className="text-gloss-gold">Required</span>, add your photos — that's genuinely all we need. Bios, write-ups, imagery, adverts, interviews: our editorial desk can craft the lot from your website, documents and photos. Write as much or as little as you like — <b>it's entirely up to you</b>.</p>
            </div>
          )}

          {active && (
            <ProgressChecklist
              items={[
                { label: "Send your story", done: !!member, target: '[data-testid="onboarding-form"]', doneNote: "With the editorial desk", todoNote: "Fill the Required basics, hit Send to the studio" },
                { label: "Add 3–5 photos", done: (member?.photos || []).length >= 3, target: '[data-testid="photos-section"]', doneNote: `${(member?.photos || []).length} uploaded`, todoNote: `${(member?.photos || []).length} of 3 uploaded so far` },
                { label: "Answer your 5 Questions", done: Object.values(member?.interview_answers || {}).some((a) => String(a || "").trim()), target: '[data-testid="interview-section"]', doneNote: "Answers with the desk", todoNote: "A rapid-fire interview — 2 minutes" },
              ]}
            />
          )}

          {/* Subscription status */}
          <div className={`mt-10 p-6 sm:p-8 border ${active ? "border-ink bg-ink text-cream" : "border-gloss-silver"}`} data-testid="subscription-status">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="overline mb-2" style={{ color: active ? "#CBA135" : "#D91023" }}>Membership</p>
                <p className="font-serif text-2xl sm:text-3xl">{active ? `Active · ${user?.plan === "monthly" ? "Monthly" : "Annual"}` : "Not active yet"}</p>
                <p className={`font-sans text-sm mt-1 ${active ? "text-cream/60" : "text-ink/60"}`}>
                  {active ? "Your content is in production on a rotating monthly schedule." : "Activate membership to get your editorial package made."}
                </p>
              </div>
              {!active && (
                <Link to="/join" className="bg-ink text-cream px-7 py-4 overline flex items-center gap-2 hover:bg-ink transition-colors shrink-0" data-testid="activate-membership">
                  <CreditCard size={16} /> Activate from £30/mo
                </Link>
              )}
            </div>
            {active && billing?.recurring && (
              <div className="mt-6 pt-5 border-t border-cream/15" data-testid="billing-panel">
                {billing.cancel_at_period_end ? (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <p className="font-sans text-sm text-cream/70" data-testid="billing-ending-note">
                      Cancellation scheduled — your membership and magazine presence run until <b className="text-cream">{fmtDate(billing.renews_at)}</b>, then your profile, features and coverage are removed from the magazine, its archive and our social channels. No further charges.
                    </p>
                    <button
                      onClick={resumeMembership}
                      disabled={billingBusy}
                      className="overline border border-gloss-gold text-gloss-gold px-5 py-2.5 hover:bg-gloss-gold hover:text-ink transition-colors shrink-0 disabled:opacity-50"
                      data-testid="resume-subscription-btn"
                    >
                      {billingBusy ? "One moment…" : "Resume membership"}
                    </button>
                  </div>
                ) : cancelConfirm ? (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="cancel-confirm-panel">
                    <p className="font-sans text-sm text-cream/70">
                      Sure? You'll keep everything until <b className="text-cream">{fmtDate(billing.renews_at)}</b> — after that your profile, features, directory listing and social posts are removed from the magazine and its archive, and billing stops.
                    </p>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={cancelMembership}
                        disabled={billingBusy}
                        className="overline border border-cream/40 text-cream/80 px-4 py-2.5 hover:border-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                        data-testid="cancel-confirm-btn"
                      >
                        {billingBusy ? "Cancelling…" : "Yes, cancel"}
                      </button>
                      <button
                        onClick={() => setCancelConfirm(false)}
                        className="overline bg-cream text-ink px-4 py-2.5 hover:bg-gloss-gold transition-colors"
                        data-testid="cancel-keep-btn"
                      >
                        Keep membership
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <p className="font-sans text-sm text-cream/70" data-testid="billing-renewal">
                      {billing.renews_at ? <>Renews automatically on <b className="text-cream">{fmtDate(billing.renews_at)}</b> · {user?.plan === "yearly" ? "£300/year" : "£30/month"}</> : "Renews automatically."}
                    </p>
                    <button
                      onClick={() => setCancelConfirm(true)}
                      className="font-sans text-xs text-cream/50 underline underline-offset-4 hover:text-cream transition-colors shrink-0"
                      data-testid="cancel-subscription-btn"
                    >
                      Cancel membership
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {active && member && (() => {
            const idx = cycleIndexFor() + 1;
            const start = editionStart(idx);
            const nextKind = member.last_feature_kind === "interview" ? "Members' Desk feature" : "Full Interview & cover shoot";
            return (
              <div className="mt-10 bg-gloss-gold/15 border border-gloss-gold/50 p-6 sm:p-8" data-testid="rotation-banner">
                <p className="overline text-ink/60 mb-2">Your VIP rotation</p>
                <p className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">You're up next for a <span className="text-gloss-gold">{nextKind}</span>.</p>
                <p className="font-sans text-base text-ink/70 mt-3">Slotted for the issue landing <b>{start.toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: "UTC" })}</b>. Every S/ME member is rotated fairly — you'll have a fresh moment in the magazine every month. Each feature runs for the full edition, then rests in the archive while your next one is prepared.</p>
              </div>
            );
          })()}

          {/* Your advert — where and when it runs */}
          {member && (
            <div className="mt-8 border border-ink/15 bg-white p-5" data-testid="portal-advert-status">
              <p className="overline text-gloss-gold mb-2">Your advert</p>
              {(() => {
                const run = member.advert_run || {};
                const WHERE = {
                  back_cover: "the Back Cover — the big band on the homepage",
                  band: "a full-width Feature Band across the magazine",
                  square: "a Square inside articles, the Directory and Noticeboard",
                  banner: "a Banner strip on the section pages",
                };
                if (run.status === "live") return <p className="font-sans text-sm text-ink/75">Live right now in <b>Issue Nº {String(issueNoFor(run.issue)).padStart(2, "0")}</b>, running as {WHERE[run.slot] || "an advert across the magazine"}. It runs for the full fortnight.</p>;
                if (run.status === "scheduled") return <p className="font-sans text-sm text-ink/75">Scheduled for <b>Issue Nº {String(issueNoFor(run.issue)).padStart(2, "0")}</b> (from {run.issue}), running as {WHERE[run.slot] || "an advert"}. It will run for the full fortnight.</p>;
                if (run.status === "ran") return <p className="font-sans text-sm text-ink/75">Your advert ran in <b>Issue Nº {String(issueNoFor(run.issue)).padStart(2, "0")}</b>. It stays part of your membership year — the editors will rotate it back into a future edition.</p>;
                return <p className="font-sans text-sm text-ink/75">Your advert is one of your membership-year drops — the editors slot it into one of the fortnightly editions (16 advert slots per edition, so every ad gets seen). The edition and placement will appear here once it's scheduled.</p>;
              })()}
            </div>
          )}

          {/* Onboarding form — pay first, then the desk opens */}
          {!active && (
            <section className="mt-12 border border-ink/20 bg-white p-8 sm:p-12 text-center" data-testid="membership-locked">
              <p className="overline text-gloss-gold mb-4">Your editorial desk is waiting</p>
              <h2 className="font-serif text-3xl sm:text-4xl leading-tight">Complete your membership to begin your feature.</h2>
              <p className="font-sans text-base text-ink/60 mt-4 max-w-xl mx-auto leading-relaxed">The moment your membership is active, this page becomes your desk — tell us your story, add your photos, answer your interview, and our editorial team takes it from there.</p>
              <Link to="/join" className="inline-flex items-center gap-2 mt-8 bg-ink text-cream px-8 py-4 overline hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="locked-join-cta">Complete membership</Link>
            </section>
          )}
          {active && (<>
          <PortalTour userKey={user?.email || "member"} />
          <section className="mt-12">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-ink/20 pb-4 mb-8">
              <h2 className="font-grotesk text-2xl sm:text-3xl font-medium">Your Profile</h2>
              <span className="flex flex-wrap items-center gap-4">
                <span className="overline text-ink/40" data-testid="draft-indicator">
                  {draftState === "saving" ? "Saving…" : draftState === "saved" ? "✓ Saved automatically" : "Auto-saves as you type"}
                </span>
                {member && <span className="overline flex items-center gap-2 text-ink/50"><Check size={14} /> {member.status === "published" ? "Live in directory" : "In editor review"}</span>}
                <button type="submit" form="profile-form" disabled={saving} className="bg-gloss-gold text-ink px-6 py-3 overline hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="send-studio-top">
                  {saving ? "Sending…" : "Send to the studio"}
                </button>
              </span>
            </div>
            <div className="border border-gloss-gold/50 bg-gloss-gold/10 p-5 mb-8 flex gap-3" data-testid="editor-review-note">
              <Check size={18} className="text-gloss-gold shrink-0 mt-0.5" />
              <p className="font-sans text-sm text-ink/75 leading-relaxed"><b>You barely have to do anything.</b> Only the fields marked <span className="text-gloss-gold font-semibold">Required</span> are needed — everything else is optional, and our editorial desk can craft it for you from your website, documents and photos. Everything you type <b>saves automatically</b>; when you're happy, hit <b>Send to the studio</b> and we take it from there.</p>
            </div>
            <div className="mb-8">
              <WebsiteImport
                self
                defaultUrl={form.website}
                onApply={(p, photos, mode, logo) => { setForm((f) => mergeProfile(f, p, mode)); setPendingPhotos(photos || []); if (logo) setPendingLogo(logo); }}
                onSelect={(photos, logo) => { setPendingPhotos(photos || []); if (logo) setPendingLogo(logo); }}
                importPhotos={member ? async (urls) => { await client.post("/members/me/import-photos", { urls }); await refresh(); } : null}
              />
            </div>
            <form onSubmit={save} noValidate id="profile-form" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6" data-testid="onboarding-form">
              <GroupHead first title="The basics" note="Who you are and where readers find you" />
              <Field label="Business name" v={form.business_name} on={(x) => { setForm({ ...form, business_name: x }); setFieldErrors((e) => ({ ...e, business_name: "" })); }} testid="f-business" required error={fieldErrors.business_name} />
              <Field label="Your name" v={form.owner_name} on={(x) => { setForm({ ...form, owner_name: x }); setFieldErrors((e) => ({ ...e, owner_name: "" })); }} testid="f-owner" required error={fieldErrors.owner_name} />
              <Combo label="Location / area" v={form.borough} on={(x) => setForm({ ...form, borough: x })} options={LONDON_AREAS} testid="f-borough" />
              <Select label="Business category" v={form.business_category} on={(x) => setForm({ ...form, business_category: x })} options={BUSINESS_CATS} testid="f-business-category" />
              <Combo label="Trade / type (type any sector, e.g. Fintech)" v={form.category} on={(x) => setForm({ ...form, category: x })} options={CATEGORIES} testid="f-category" />
              <Field label="Full business address" v={form.address} on={(x) => { setForm({ ...form, address: x }); setFieldErrors((e) => ({ ...e, address: "" })); }} testid="f-address" required error={fieldErrors.address} />
              <label className="flex items-center gap-2.5 sm:col-span-2 cursor-pointer" data-testid="f-display-address">
                <input type="checkbox" checked={form.display_address} onChange={(e) => setForm({ ...form, display_address: e.target.checked })} className="w-4 h-4 accent-ink" />
                <span className="font-sans text-sm text-ink/70">Show my business address in my published articles &amp; campaigns</span>
              </label>
              <GroupHead title="Contact & socials" note="How readers and the desk reach you" />
              <Field label="Website (optional — leave blank if you don't have one)" v={form.website} on={(x) => { setForm({ ...form, website: x }); setFieldErrors((e) => ({ ...e, website: "" })); }} testid="f-website" error={fieldErrors.website} />
              <Field label="Email" v={form.email} on={(x) => { setForm({ ...form, email: x }); setFieldErrors((e) => ({ ...e, email: "" })); }} testid="f-email" required error={fieldErrors.email} />
              <Field label="Mobile number" v={form.mobile} on={(x) => { setForm({ ...form, mobile: x }); setFieldErrors((e) => ({ ...e, mobile: "" })); }} testid="f-mobile" required error={fieldErrors.mobile} />
              <Field label="Landline (optional)" v={form.landline} on={(x) => setForm({ ...form, landline: x })} testid="f-landline" />
              <label className="flex items-center gap-2.5 sm:col-span-2 cursor-pointer" data-testid="f-display-phone">
                <input type="checkbox" checked={form.display_phone} onChange={(e) => setForm({ ...form, display_phone: e.target.checked })} className="w-4 h-4 accent-ink" />
                <span className="font-sans text-sm text-ink/70">Show my phone number(s) in my published campaigns</span>
              </label>
              <label className="flex items-center gap-2.5 sm:col-span-2 cursor-pointer -mt-3" data-testid="f-use-landline">
                <input type="checkbox" checked={form.use_landline} onChange={(e) => setForm({ ...form, use_landline: e.target.checked })} className="w-4 h-4 accent-ink" />
                <span className="font-sans text-sm text-ink/70">Also use my landline in campaigns</span>
              </label>
              <Field label="Instagram handle (optional — we tag you and invite you to collaborate when we post you)" v={form.instagram} on={(x) => setForm({ ...form, instagram: x })} testid="f-instagram" />
              <Field label="Facebook page (optional — mentioned whenever we share your features)" v={form.facebook} on={(x) => setForm({ ...form, facebook: x })} testid="f-facebook" />
              <Field label="TikTok (optional)" v={form.tiktok} on={(x) => setForm({ ...form, tiktok: x })} testid="f-tiktok" />
              <Field label="LinkedIn (optional)" v={form.linkedin} on={(x) => setForm({ ...form, linkedin: x })} testid="f-linkedin" />
              <GroupHead title="Your story" note="All optional — leave anything blank and the desk writes it for you" />
              <Area label="Personal bio — about you (optional — or let the desk write it)" v={form.personal_bio} on={(x) => setForm({ ...form, personal_bio: x })} testid="f-personal-bio" className="sm:col-span-2" />
              <div className="sm:col-span-2 -mt-3"><PolishBtn field="personal_bio" /></div>
              <Area label="Business bio — about the business (optional — or let the desk write it)" v={form.business_bio} on={(x) => setForm({ ...form, business_bio: x })} testid="f-business-bio" className="sm:col-span-2" />
              <div className="sm:col-span-2 -mt-3"><PolishBtn field="business_bio" /></div>
              <Area label="Services / specialities (optional — or let the desk write it)" v={form.services} on={(x) => setForm({ ...form, services: x })} testid="f-services" className="sm:col-span-2" />
              <div className="sm:col-span-2 -mt-3"><PolishBtn field="services" /></div>
              <Field label="Reader offer (optional)" v={form.offers} on={(x) => setForm({ ...form, offers: x })} testid="f-offers" className="sm:col-span-2" />
              <Area label="Links & notes for our editors (paste press links, anything that describes you — we'll read it all to craft your feature)" v={form.ref_notes} on={(x) => setForm({ ...form, ref_notes: x })} testid="f-ref-notes" className="sm:col-span-2" />
              <div className="sm:col-span-2">
                {Object.values(fieldErrors).some(Boolean) && (
                  <div className="border border-red-500/60 bg-red-50 p-4 mb-5 flex gap-3" data-testid="form-error-summary">
                    <X size={16} className="text-red-500 shrink-0 mt-0.5" />
                    <p className="font-sans text-sm text-red-700">A few required fields are missing — please check the highlighted fields above.</p>
                  </div>
                )}
                {justSaved && (
                  <div className="border border-emerald-600/50 bg-emerald-50 p-4 mb-5 flex gap-3" data-testid="save-success-banner">
                    <Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                    <p className="font-sans text-sm text-emerald-800"><b>Sent to the studio</b> — your profile is with our editorial desk. We'll review, style and polish everything before it goes live.</p>
                  </div>
                )}
                {member && (((member.photos || []).length + pendingPhotos.length) < 3 || !member.imagery_preference) && (
                  <p className="font-mono text-[11px] text-gloss-gold mb-3" data-testid="send-gate-note">
                    Before this reaches the studio: {((member.photos || []).length + pendingPhotos.length) < 3 ? `add at least 3 photos (${(member.photos || []).length + pendingPhotos.length} so far)` : ""}{((member.photos || []).length + pendingPhotos.length) < 3 && !member.imagery_preference ? " · " : ""}{!member.imagery_preference ? "choose how your photos are handled" : ""}
                  </p>
                )}
                <button type="submit" disabled={saving} className="bg-gloss-gold text-ink px-8 py-4 overline hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="save-profile">
                  {saving ? "Sending…" : member ? "Send updates to the studio" : "Send to the studio"}
                </button>
                <p className="font-sans text-xs text-ink/45 mt-3">Everything you type is already saved automatically — this sends it to our editorial desk.</p>
                <p className="font-sans text-xs text-ink/45 mt-3 max-w-2xl leading-relaxed" data-testid="editorial-discretion-note">By sending your details to the studio, you're entrusting our editorial team to craft and publish your features. We use full editorial discretion over content, styling, timing and placement — always with one aim: presenting you and your business at their absolute best.</p>
              </div>
            </form>
          </section>

          {/* Reference documents for the editorial desk */}
          <section className="mt-14" data-testid="ref-docs-section">
            <h2 className="font-grotesk text-2xl sm:text-3xl font-medium">Reference documents <span className="text-ink/40 text-lg">(optional)</span></h2>
            <p className="font-sans text-sm text-ink/55 mt-2 max-w-2xl">Upload brochures, menus, pitch decks, price lists or notes (PDF, DOCX or TXT). Our editorial team reads them so your feature is written from your real material — not guesswork.</p>
            <label className="inline-flex items-center gap-2 mt-4 border border-ink/40 px-6 py-3.5 overline cursor-pointer hover:bg-ink hover:text-cream transition-colors" data-testid="portal-upload-docs">
              {uploadingDocs ? <Clock size={16} className="animate-pulse" /> : <Upload size={16} />} {uploadingDocs ? "Uploading…" : "Upload documents"}
              <input type="file" accept=".pdf,.docx,.txt,.md,.csv" multiple className="hidden" onChange={(e) => uploadRefDocs(e.target.files)} disabled={uploadingDocs || !member} />
            </label>
            {(member?.ref_docs || []).length > 0 && (
              <ul className="mt-4 space-y-2 max-w-2xl" data-testid="portal-docs-list">
                {(member.ref_docs || []).map((d) => (
                  <li key={d.id} className="flex items-center gap-3 text-sm border border-ink/15 bg-white px-4 py-3" data-testid={`portal-doc-${d.id}`}>
                    <FileText size={16} className="text-ink/50 shrink-0" />
                    <span className="font-sans truncate flex-1">{d.name}</span>
                    <span className="font-mono text-[10px] text-ink/40 shrink-0">{d.chars > 0 ? `${d.chars} chars` : "no text found"}</span>
                    <button onClick={() => deleteRefDoc(d.id)} className="text-ink/40 hover:text-red-500 transition-colors shrink-0" title="Remove" data-testid={`portal-doc-del-${d.id}`}><X size={16} /></button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Photo upload */}
          <section className="mt-14" data-testid="photos-section">
            <div className="border-b border-ink/20 pb-4 mb-6">
              <h2 className="font-grotesk text-2xl sm:text-3xl font-medium">Your Photos <span className="text-ink/40 text-lg">(min 3, max 5)</span></h2>
              <p className="font-sans text-sm text-ink/60 mt-2">Upload <b>at least 3 and up to 5</b> clear photos of yourself (different angles help). Our studio tunes them into photorealistic, editorial-grade magazine portraits that still look 100% like you.</p>
            </div>
            {/* 1. UPLOAD FIRST */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {photos.map((p, i) => (
                <div key={i} className="relative group aspect-[2/3] bg-ink overflow-hidden">
                  <img src={`${client.defaults.baseURL}/files/${p}`} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={async () => {
                    try { await client.post("/members/me/photos/remove", { path: p, field: "photos" }); await refresh(); toast.success("Photo removed."); }
                    catch { toast.error("Could not remove that photo."); }
                  }} className="absolute top-2 right-2 bg-black/60 text-cream p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-all" title="Remove this photo" data-testid={`remove-photo-${i}`}>
                    <X size={14} />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className={`aspect-[2/3] border border-dashed border-ink/40 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-gloss-silver transition-colors ${!member ? "opacity-40 pointer-events-none" : ""}`} data-testid="photo-upload">
                  <Upload size={22} strokeWidth={1.5} />
                  <span className="overline text-ink/60">{uploading ? "Uploading…" : "Add"}</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} disabled={uploading || !member} />
                </label>
              )}
            </div>
            {!member && <p className="font-mono text-xs text-ink/50 mt-3">Save your profile to enable uploads.</p>}

            {/* 2. STYLE OPTIONS AFTER */}
            {member && (
              <div className="mt-10" data-testid="imagery-preference">
                <p className="overline text-ink/60 mb-1">How would you like your photos handled? <span className="text-gloss-gold">Required</span></p>
                <p className="font-sans text-xs text-ink/50 mb-3">Pick one before your profile can reach the studio.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                  {[
                    { v: "studio", t: "Let the studio style my shots", d: "Our studio turns your photos into polished, editorial magazine portraits." },
                    { v: "own", t: "Use my photos as they are", d: "You're happy with your own photography — we'll use it untouched." },
                  ].map((o) => {
                    const cur = member.imagery_preference;
                    const on = cur === o.v;
                    return (
                      <button key={o.v} type="button" onClick={async () => {
                        try { await client.post("/members/me/imagery-preference", { imagery_preference: o.v }); await refresh(); toast.success(o.v === "own" ? "Noted — the desk will use your own photos." : "Noted — the studio will style your shots."); }
                        catch { toast.error("Could not save preference."); }
                      }} className={`text-left border p-4 transition-colors ${on ? "border-gloss-gold bg-gloss-gold/10" : "border-ink/20 hover:border-ink/50"}`} data-testid={`imagery-pref-${o.v}`}>
                        <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full border mb-2 ${on ? "bg-gloss-gold border-gloss-gold" : "border-ink/40"}`} />
                        <p className="font-sans text-sm font-medium">{o.t}</p>
                        <p className="font-sans text-xs text-ink/50 mt-1">{o.d}</p>
                      </button>
                    );
                  })}
                </div>
                <p className="overline text-ink/60 mb-3 mt-8">Who should star in your imagery?</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
                  {[
                    { v: "people", t: "Me, front and centre", d: "Your face leads every feature — portraits and at-work shots." },
                    { v: "balanced", t: "A balanced mix", d: "Some of you, some of your space, products and brand." },
                    { v: "brand", t: "My brand & space only", d: "No portraits — your venue, products and craft take the spotlight." },
                  ].map((o) => {
                    const cur = member.imagery_focus || "balanced";
                    const on = cur === o.v;
                    return (
                      <button key={o.v} type="button" onClick={async () => {
                        try { await client.post("/members/me/imagery-focus", { imagery_focus: o.v }); await refresh(); toast.success("Noted — the desk will style your features this way."); }
                        catch { toast.error("Could not save preference."); }
                      }} className={`text-left border p-4 transition-colors ${on ? "border-gloss-gold bg-gloss-gold/10" : "border-ink/20 hover:border-ink/50"}`} data-testid={`imagery-focus-${o.v}`}>
                        <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full border mb-2 ${on ? "bg-gloss-gold border-gloss-gold" : "border-ink/40"}`} />
                        <p className="font-sans text-sm font-medium">{o.t}</p>
                        <p className="font-sans text-xs text-ink/50 mt-1">{o.d}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {member && <StudioReshoot refresh={refresh} photosCount={photos.length} />}
            {member && (
              <div className="mt-6 border-t border-ink/15 pt-6">
                <p className="font-sans text-sm text-ink/70 mb-3">Prefer your own photo? Skip the studio shoot and upload your own <b>Editorial</b> or <b>Cover Member</b> shot directly.</p>
                <div className="flex flex-wrap gap-3">
                  <label className="inline-flex items-center gap-2 border border-ink/40 px-5 py-3 overline cursor-pointer hover:border-gloss-silver transition-colors" data-testid="upload-own-editorial">
                    <Upload size={16} /> {uploadingCover === "cover_image" ? "Uploading…" : "Upload own Editorial cover"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadOwnCover(e.target.files, "cover_image")} disabled={!!uploadingCover} />
                  </label>
                  <label className="inline-flex items-center gap-2 border border-ink/40 px-5 py-3 overline cursor-pointer hover:border-gloss-silver transition-colors" data-testid="upload-own-directory">
                    <Upload size={16} /> {uploadingCover === "directory_image" ? "Uploading…" : "Upload own Cover Member shot"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadOwnCover(e.target.files, "directory_image")} disabled={!!uploadingCover} />
                  </label>
                </div>
              </div>
            )}
          </section>

          {/* Office / Venue photos */}
          <section className="mt-14" data-testid="venue-section">
            <div className="border-b border-ink/20 pb-4 mb-6">
              <h2 className="font-grotesk text-2xl sm:text-3xl font-medium">Your Office / Venue <span className="text-ink/40 text-lg">(optional)</span></h2>
              <p className="font-sans text-sm text-ink/60 mt-2">Upload up to 5 photos of your space. Our studio can turn them into styled, editorial S/ME interior shots — used across your features.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {(member?.venue_photos || []).map((p, i) => (
                <div key={i} className="aspect-square bg-ink overflow-hidden">
                  <img src={`${client.defaults.baseURL}/files/${p}`} alt={`Venue ${i + 1}`} className="w-full h-full object-cover" data-testid={`venue-photo-${i}`} />
                </div>
              ))}
              {(member?.venue_photos || []).length < 5 && (
                <label className={`aspect-square border border-dashed border-ink/40 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-gloss-silver transition-colors ${!member ? "opacity-40 pointer-events-none" : ""}`} data-testid="venue-upload">
                  <Upload size={22} strokeWidth={1.5} />
                  <span className="overline text-ink/60">{uploadingVenue ? "Uploading…" : "Add"}</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadVenue(e.target.files)} disabled={uploadingVenue || !member} />
                </label>
              )}
            </div>
            {member && (
              <div className="mt-6">
                <button onClick={genVenue} disabled={makingVenue} className="inline-flex items-center gap-2 bg-ink text-cream px-6 py-3.5 overline hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid="generate-venue">
                  <Sparkles size={16} className={makingVenue ? "animate-pulse" : ""} /> {makingVenue ? "Styling your venue…" : "Generate my venue imagery"}
                </button>
                {(member?.venue_gallery || []).length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-6">
                    {member.venue_gallery.map((g, i) => (
                      <div key={i} className="aspect-[3/2] bg-ink overflow-hidden"><img src={`${client.defaults.baseURL}/files/${g.image}`} alt={g.slug} className="w-full h-full object-cover" data-testid={`venue-gallery-${i}`} /></div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Work & product photos */}
          <section className="mt-14" data-testid="work-photos-section">
            <div className="border-b border-ink/20 pb-4 mb-6">
              <h2 className="font-grotesk text-2xl sm:text-3xl font-medium">Your Work & Products <span className="text-ink/40 text-lg">(up to 20)</span></h2>
              <p className="font-sans text-sm text-ink/60 mt-2">Product shots, your work, behind the scenes — anything you'd like the magazine to use in your features, adverts and articles. These are separate from your 5 portraits, so add as many as you like (up to 20).</p>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {(member?.work_photos || []).map((p, i) => (
                <div key={p} className="relative group aspect-square bg-ink overflow-hidden">
                  <img src={`${client.defaults.baseURL}/files/${p}`} alt={`Work ${i + 1}`} className="w-full h-full object-cover" data-testid={`work-photo-${i}`} />
                  <button type="button" onClick={async () => {
                    try { await client.post("/members/me/photos/remove", { path: p, field: "work_photos" }); await refresh(); toast.success("Removed."); }
                    catch { toast.error("Could not remove that photo."); }
                  }} className="absolute top-1.5 right-1.5 bg-black/60 text-cream p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-all" title="Remove" data-testid={`work-photo-remove-${i}`}>
                    <X size={13} />
                  </button>
                </div>
              ))}
              {(member?.work_photos || []).length < 20 && (
                <label className={`aspect-square border border-dashed border-ink/40 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-gloss-silver transition-colors ${!member ? "opacity-40 pointer-events-none" : ""}`} data-testid="work-photo-upload">
                  <Upload size={20} strokeWidth={1.5} />
                  <span className="overline text-ink/60">{uploadingWork ? "Uploading…" : "Add"}</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadWork(e.target.files)} disabled={uploadingWork || !member} />
                </label>
              )}
            </div>
          </section>

          {/* 5 Questions interview */}
          {member && (
            <section className="mt-14" data-testid="interview-section">
              <div className="border-b border-ink/20 pb-4 mb-6">
                <h2 className="font-grotesk text-2xl sm:text-3xl font-medium">Your Interview — "5 Questions"</h2>
                <p className="font-sans text-sm text-ink/60 mt-2">Answer these in your own words. We'll shape the best of them into your "5 Questions" feature — one lands in your rotation each month.</p>
              </div>
              <div className="space-y-6">
                {questions.map((qn, i) => (
                  <div key={i}>
                    <label className="overline text-ink/60">{i + 1}. {qn}</label>
                    <textarea
                      value={answers[String(i)] || ""}
                      onChange={(e) => setAnswers({ ...answers, [String(i)]: e.target.value })}
                      rows={2}
                      className="w-full bg-transparent border-b border-ink/30 py-2 mt-1.5 font-sans text-base focus:outline-none focus:border-gloss-silver transition-colors resize-none"
                      data-testid={`interview-q-${i}`}
                    />
                  </div>
                ))}
                <button onClick={saveInterview} disabled={savingInt} className="bg-ink text-cream px-8 py-4 overline hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="save-interview">
                  {savingInt ? "Saving…" : "Save my answers"}
                </button>
              </div>
            </section>
          )}

          {/* Company logo */}
          <section className="mt-14">
            <div className="border-b border-ink/20 pb-4 mb-6">
              <h2 className="font-grotesk text-2xl sm:text-3xl font-medium">Your Company Logo</h2>
              <p className="font-sans text-sm text-ink/60 mt-2">Upload your brand logo (a white or transparent PNG works best). This is what represents you in the S/ME Directory.</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="w-44 h-28 bg-ink flex items-center justify-center p-4 shrink-0">
                {logoUrl(member) ? (
                  <img src={logoUrl(member)} alt="Your logo" className="max-h-full max-w-full object-contain" data-testid="current-logo" />
                ) : (
                  <span className="font-mono text-xs text-cream/40 uppercase tracking-widest">No logo yet</span>
                )}
              </div>
              <label className={`inline-flex items-center gap-3 border border-ink/40 px-6 py-4 cursor-pointer hover:border-gloss-silver transition-colors ${!member ? "opacity-40 pointer-events-none" : ""}`} data-testid="logo-upload">
                <ImageIcon size={18} strokeWidth={1.5} />
                <span className="overline">{uploadingLogo ? "Uploading…" : logoUrl(member) ? "Replace logo" : "Upload logo"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files)} disabled={uploadingLogo || !member} />
              </label>
            </div>
          </section>

          {/* Package status */}
          <section className="mt-14">
            <h2 className="font-grotesk text-2xl sm:text-3xl font-medium border-b border-ink/20 pb-4 mb-6">Your S/ME Package</h2>
            {hasPackage ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <PackageTile ready label="Editorial Feature" text={pkg.feature?.title} />
                <PackageTile ready={!!pkg.advert} label="Magazine Advert" text={pkg.advert?.headline} />
                <PackageTile ready={!!pkg.five_questions} label='"5 Questions"' text={pkg.five_questions?.title} />
                <PackageTile ready={!!pkg.social} label="Social Post" text="Instagram + TikTok ready" />
                <PackageTile ready={!!member?.cover_image} label="S/ME Cover Shoot" text={member?.cover_image ? "Cover ready" : "Awaiting your photos"} />
                {member?.status === "published" && (
                  <Link to={`/member/${member.id}`} className="bg-ink text-cream p-6 flex items-center gap-3 hover:bg-ink transition-colors">
                    <Sparkles size={18} className="text-gloss-gold" /> <span className="overline">View your live profile</span>
                  </Link>
                )}
              </div>
            ) : (
              <div className="bg-secondary p-8 border border-ink/15">
                <p className="font-sans text-ink/70">Your package hasn't been created yet. Once you've completed your profile and uploaded photos, our editorial team crafts everything and reviews it before it goes live.</p>
              </div>
            )}
          </section>

          {/* Personalised year plan */}
          <section className="mt-14 mb-10">
            <h2 className="font-grotesk text-2xl sm:text-3xl font-medium border-b border-ink/20 pb-4 mb-3">Your Personal Year Plan</h2>
            <p className="font-sans text-sm text-ink/60 mb-6 max-w-2xl">
              Every member's year is sequenced differently — so the magazine always feels fresh. Here's what we've lined up for you and when it lands.
            </p>
            <NextDrop plan={yearPlan} notify={notify} onToggle={toggleNotify} member={member} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="year-plan">
              {yearPlan.map((d, i) => (
                <div key={i} className={`border p-5 ${d.delivered ? "border-ink bg-ink text-cream" : "border-ink/20"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="overline" style={{ color: d.delivered ? "#CBA135" : undefined }}>{d.label}</p>
                    {d.delivered ? <Check size={14} className="text-gloss-gold" /> : <Clock size={14} className="text-ink/40" />}
                  </div>
                  <p className={`font-serif text-xl leading-tight ${d.delivered ? "" : "text-ink"}`}>{d.item}</p>
                  <p className={`font-mono text-[10px] uppercase tracking-wider mt-2 ${d.delivered ? "text-cream/50" : "text-ink/45"}`}>{d.delivered ? "Delivered" : "Scheduled"}</p>
                </div>
              ))}
            </div>
            <p className="font-mono text-xs text-ink/50 mt-4">This is your predicted schedule — our editors may bring a drop forward when the timing's right.</p>
          </section>

          {/* Share kit — ready-made social cards for every published feature */}
          {member && <ShareKit />}

          {/* Billing & invoices — record of every membership payment */}
          <BillingSection />

          {/* Why S/ME is your SEO + GEO engine */}
          <section className="mt-14 bg-ink text-cream p-8 sm:p-12" data-testid="seo-geo-benefits">
            <p className="overline text-gloss-gold mb-4">Why this gets you found</p>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight max-w-3xl">Every S/ME feature is a permanent SEO + GEO asset — not just your column.</h2>
            <p className="font-sans text-base text-cream/75 mt-5 max-w-3xl leading-relaxed">
              People search for you on Google, and increasingly they ask answer engines (ChatGPT, Gemini, Perplexity) "who's the best {form.category || "business"} in {form.borough || "your area"}?" S/ME makes sure the answer is <span className="font-serif italic">you</span>. Your whole presence works, not only the Members' Desk blog: your <b>profile</b>, <b>feature</b>, <b>Spotlight</b>, <b>The Edit</b>, <b>Interview</b>, <b>advert</b> and <b>Directory listing</b> are all indexed, structured and interlinked, so search and answer engines have real, quotable content about your business.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-9">
              {[
                { icon: Search, t: "SEO — found on Google", d: "Clean, structured, keyword-rich editorial across multiple pages, each optimised and cross-linked to your profile." },
                { icon: Globe, t: "GEO — cited by answer engines", d: "Written so answer engines can quote you when readers ask for the best in your field and area." },
                { icon: TrendingUp, t: "It compounds", d: "New content every month across a full year means your footprint keeps growing. The longer you're in, the more you're found." },
              ].map((b) => (
                <div key={b.t} className="border border-cream/15 p-6">
                  <b.icon size={22} className="text-gloss-gold mb-4" strokeWidth={1.5} />
                  <p className="font-grotesk text-lg font-medium">{b.t}</p>
                  <p className="font-sans text-sm text-cream/65 mt-2 leading-relaxed">{b.d}</p>
                </div>
              ))}
            </div>
            <p className="font-mono text-[11px] text-cream/45 mt-8 max-w-3xl">The Members' Desk is one part of it. A column shows your expertise and adds another indexed, machine-readable page in your name — but it's the full magazine presence that does the heavy lifting.</p>
          </section>
          </>)}
        </div>
      </div>
    </MagazineLayout>
  );
}

const YEAR_PLAN_POOL = [
  "Editorial Feature", "Magazine Advert", "\"5 Questions\" Interview", "Instagram Reel Script",
  "Members' Desk Column", "Cover Reshoot", "Noticeboard Spotlight", "Social Quote Card",
  "Season Trend Feature", "Podcast Mention", "Year-in-Review Retrospective",
];

function StudioReshoot({ refresh, photosCount }) {
  const [info, setInfo] = useState(null);
  const [working, setWorking] = useState(false);
  const [picking, setPicking] = useState("");
  useEffect(() => { client.get("/members/me/reshoot").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  const request = async () => {
    setWorking(true);
    try {
      const r = await client.post("/members/me/reshoot", {});
      setInfo(r.data);
      toast.success("The studio has developed a new shot — pick your favourite below.");
    } catch (e) { toast.error(e.response?.data?.detail || "The studio couldn't complete that — please try again."); }
    setWorking(false);
  };
  const select = async (id) => {
    setPicking(id);
    try {
      const r = await client.post("/members/me/reshoot/select", { id });
      setInfo((p) => ({ ...p, cover_image: r.data.cover_image }));
      await refresh();
      toast.success("That's your new editorial cover.");
    } catch { toast.error("Couldn't set that shot — try again."); }
    setPicking("");
  };
  const remaining = info?.remaining ?? 0;
  return (
    <div className="mt-8 border border-ink/15 bg-white p-5 sm:p-6" data-testid="studio-reshoot">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline text-gloss-gold">The Studio · your cover shoots</p>
          <p className="font-sans text-sm text-ink/60 mt-1">Request a shoot and our studio styles a fresh editorial portrait from your photos. Keep your favourite as your cover.</p>
        </div>
        {info && <span className="font-mono text-xs text-ink/55 border border-ink/20 px-3 py-1.5" data-testid="reshoot-remaining">{remaining} of {info.monthly_limit} studio sessions left this month</span>}
      </div>
      <button onClick={request} disabled={working || photosCount === 0 || remaining === 0} className="mt-4 inline-flex items-center gap-2 bg-ink text-cream px-6 py-3.5 overline hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid="request-reshoot">
        <Sparkles size={16} className={working ? "animate-pulse" : ""} /> {working ? "The studio is developing your shot…" : "Request a studio reshoot"}
      </button>
      <p className="font-mono text-[11px] text-ink/45 mt-2">
        {photosCount === 0 ? "Upload at least one photo first — the studio shoots from them." : remaining === 0 ? "You've used this month's studio time — more sessions unlock on the 1st." : "Each shoot takes about a minute. Nothing changes until you pick a favourite."}
      </p>
      {(info?.gallery || []).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-5" data-testid="reshoot-gallery">
          {[...info.gallery].reverse().map((g) => {
            const isCover = g.image === info.cover_image;
            return (
              <div key={g.id} className={`border-2 ${isCover ? "border-gloss-gold" : "border-transparent"}`} data-testid={`reshoot-shot-${g.id}`}>
                <div className="aspect-[2/3] bg-ink overflow-hidden">
                  <img src={`${client.defaults.baseURL}/files/${g.image}`} alt="Studio shot" className="w-full h-full object-cover" />
                </div>
                <button onClick={() => select(g.id)} disabled={!!picking || isCover} className={`w-full overline py-2 border border-t-0 ${isCover ? "bg-gloss-gold text-ink border-gloss-gold" : "border-ink/20 hover:bg-ink hover:text-cream"} transition-colors disabled:opacity-70`} data-testid={`reshoot-pick-${g.id}`}>
                  {isCover ? "Your cover" : picking === g.id ? "Setting…" : "Use as my cover"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function buildYearPlan(member) {
  const seedStr = member?.id || member?.business_name || "sme";
  let offset = 0;
  for (let i = 0; i < seedStr.length; i++) offset = (offset * 31 + seedStr.charCodeAt(i)) % YEAR_PLAN_POOL.length;
  const start = new Date(member?.published_at || member?.created_at || Date.now());
  const now = new Date();
  const elapsed = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    return {
      label: `Month ${i + 1} · ${d.toLocaleString("en-GB", { month: "short", year: "2-digit" })}`,
      item: YEAR_PLAN_POOL[(offset + i) % YEAR_PLAN_POOL.length],
      date: d,
      delivered: i <= elapsed,
    };
  });
}

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = Math.max(0, new Date(target).getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { days, hours, mins, secs };
}

function NextDrop({ plan, notify, onToggle, member }) {
  const next = plan.find((p) => !p.delivered);
  const cd = useCountdown(next?.date);
  if (!member) return null;
  if (!next) return (
    <div className="mt-14 border border-ink/20 p-6 sm:p-8" data-testid="next-drop">
      <p className="overline text-gloss-silver mb-2">Your year</p>
      <p className="font-serif text-2xl">Your full year of coverage has landed — a fresh cycle begins next month.</p>
    </div>
  );
  return (
    <div className="mt-14 bg-ink text-cream p-6 sm:p-8 grain" data-testid="next-drop">
      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <p className="overline text-gloss-gold mb-2">Your next drop</p>
          <p className="font-serif text-3xl sm:text-4xl tracking-tight leading-none">{next.item}</p>
          <p className="font-sans text-sm text-cream/60 mt-2">Scheduled for {next.date.toLocaleString("en-GB", { month: "long", year: "numeric" })}</p>
        </div>
        <div className="flex items-center gap-3" data-testid="countdown">
          {[["Days", cd.days], ["Hrs", cd.hours], ["Min", cd.mins], ["Sec", cd.secs]].map(([l, val]) => (
            <div key={l} className="text-center border border-cream/25 px-3 sm:px-4 py-3 min-w-[58px]">
              <p className="font-serif text-2xl sm:text-3xl leading-none">{String(val).padStart(2, "0")}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-cream/50 mt-1.5">{l}</p>
            </div>
          ))}
        </div>
      </div>
      <button onClick={onToggle} data-testid="notify-toggle"
        className={`relative z-10 mt-6 inline-flex items-center gap-2 px-6 py-3 overline transition-colors ${notify ? "bg-gloss-gold text-ink" : "border border-cream/60 hover:bg-cream hover:text-ink"}`}>
        {notify ? <><Check size={14} /> You'll be emailed when it's ready</> : <><Bell size={14} /> Email me when it's ready</>}
      </button>
    </div>
  );
}

function PackageTile({ ready, label, text }) {
  return (
    <div className={`p-6 border ${ready ? "border-ink" : "border-ink/20 bg-secondary"}`}>
      <div className="flex items-center gap-2 mb-2">
        {ready ? <Check size={16} className="text-gloss-silver" /> : <Clock size={16} className="text-ink/40" />}
        <p className="overline">{label}</p>
      </div>
      <p className="font-serif text-lg leading-tight">{text || "Pending"}</p>
    </div>
  );
}

function Field({ label, v, on, required, testid, className = "", error }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline text-ink/60 block mb-2">{label}{required && <span className="text-gloss-gold"> · Required</span>}</span>
      <input value={v} onChange={(e) => on(e.target.value)} required={required} data-testid={testid} spellCheck={true}
        className={`w-full bg-white border px-3.5 py-3 font-sans text-base focus:outline-none transition-colors ${error ? "border-red-500" : "border-ink/25 focus:border-ink"}`} />
      {error && <span className="block font-sans text-xs text-red-600 mt-1.5" data-testid={`${testid}-error`}>{error}</span>}
    </label>
  );
}
function Area({ label, v, on, required, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline text-ink/60 block mb-2">{label}</span>
      <textarea value={v} onChange={(e) => on(e.target.value)} required={required} rows={3} data-testid={testid} spellCheck={true}
        className="w-full bg-white border border-ink/25 p-3.5 font-sans text-base focus:outline-none focus:border-ink transition-colors resize-none" />
    </label>
  );
}
function Select({ label, v, on, options, testid }) {
  return (
    <label className="block">
      <span className="overline text-ink/60 block mb-2">{label}</span>
      <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-ink/25 px-3 py-3 font-sans text-base focus:outline-none focus:border-ink transition-colors">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Combo({ label, v, on, options, testid }) {
  const listId = `${testid}-list`;
  return (
    <label className="block">
      <span className="overline text-ink/60 block mb-2">{label}</span>
      <input value={v} onChange={(e) => on(e.target.value)} list={listId} data-testid={testid} placeholder="Type or pick your area — or add your own"
        className="w-full bg-white border border-ink/25 px-3.5 py-3 font-sans text-base focus:outline-none focus:border-ink transition-colors" />
      <datalist id={listId}>{options.map((o) => <option key={o} value={o} />)}</datalist>
    </label>
  );
}

function GroupHead({ title, note, first = false }) {
  return (
    <div className={`sm:col-span-2 ${first ? "" : "mt-6"}`}>
      <p className="font-grotesk text-lg font-medium">{title}</p>
      {note && <p className="font-sans text-xs text-ink/50 mt-0.5">{note}</p>}
      <div className="h-px bg-ink/10 mt-3" />
    </div>
  );
}

function BillingSection() {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { client.get("/members/me/invoices").then((r) => setInvoices(r.data)).catch(() => {}); }, []);
  const printReceipt = (inv) => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>${inv.number} — S/ME receipt</title><style>body{font-family:Georgia,serif;max-width:640px;margin:60px auto;color:#111;padding:0 20px}h1{font-size:44px;margin:0}.muted{color:#777;font-size:11px;text-transform:uppercase;letter-spacing:2px}.row{display:flex;justify-content:space-between;border-bottom:1px solid #ddd;padding:12px 0;font-size:15px}</style></head><body><h1>S/ME</h1><p class="muted">Receipt · ${inv.number}</p><div class="row"><span>Date</span><b>${new Date(inv.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</b></div><div class="row"><span>Item</span><b>${inv.plan}</b></div><div class="row"><span>Status</span><b>${inv.status}</b></div><div class="row"><span>Total</span><b>£${Number(inv.amount).toFixed(2)} ${inv.currency}</b></div><p class="muted" style="margin-top:44px">Paid securely via Stripe. Thank you for being part of S/ME.</p></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };
  if (!invoices.length) return null;
  return (
    <section className="mt-14" data-testid="billing-section">
      <h2 className="font-grotesk text-2xl sm:text-3xl font-medium border-b border-ink/20 pb-4 mb-3">Billing &amp; invoices</h2>
      <p className="font-sans text-sm text-ink/60 mb-6 max-w-2xl">Every payment on your membership, with a printable receipt. Payments are processed securely by Stripe.</p>
      <div className="border border-ink/15">
        {invoices.map((inv) => (
          <div key={inv.number} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-ink/10 last:border-b-0" data-testid={`invoice-${inv.number}`}>
            <div className="min-w-[140px]">
              <p className="font-mono text-[11px] text-ink/45">{inv.number}</p>
              <p className="font-sans text-sm font-medium">{inv.plan}</p>
            </div>
            <p className="font-mono text-xs text-ink/50">{new Date(inv.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
            <span className="overline text-[9px] bg-ink text-cream px-2 py-0.5">Paid</span>
            <p className="font-serif text-lg">£{Number(inv.amount).toFixed(2)}</p>
            <button onClick={() => printReceipt(inv)} className="overline border border-ink/40 px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors" data-testid={`receipt-btn-${inv.number}`}>Receipt</button>
          </div>
        ))}
      </div>
    </section>
  );
}
